import { useState, useMemo } from 'react';
import { useUserContext } from '../context/UserContext';
import { useStoresQuery } from '../hooks/useStoresQuery';
import { ErrorMessage } from '../components/ErrorMessage';
import { TreeView } from '../components/TreeView';
import { JsonViewer } from '../components/JsonViewer';
import { EmptyState, EmptySearchState, Autocomplete } from '../components/ui';
import { Skeleton } from '../components/ui';
import { buildStoreHierarchyTree, createMappStoreHierarchyOptions } from '@mapp-byte-connect/config';
import {
  Search,
  Grid3X3,
  List,
  TreeDeciduous,
  Store as StoreIcon,
  MapPin,
  Building2,
  ChevronDown,
  ChevronRight,
  Globe,
} from 'lucide-react';
import type { Store } from '../api';

type ViewMode = 'grid' | 'list' | 'tree' | 'json';

interface StoreCardProps {
  store: Store;
}

function StoreCard({ store }: StoreCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3 gap-2">
        <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
          <StoreIcon className="w-5 h-5 text-gray-600" />
        </div>
        <div className="flex flex-col items-end gap-0.5 min-w-0">
          <span
            className="text-xs font-medium text-gray-700 bg-gray-100 px-2 py-1 rounded truncate max-w-full"
            title={store.champsNumber ? `Champs #${store.champsNumber}` : undefined}
          >
            #{store.champsNumber || store.internalStoreNumber}
          </span>
          {store.champsNumber && (
            <span className="text-[10px] text-gray-400">AIS #{store.internalStoreNumber}</span>
          )}
        </div>
      </div>
      <h3 className="font-semibold text-gray-900 mb-1 truncate" title={store.storeName}>
        {store.storeName}
      </h3>
      <div className="flex items-center gap-1.5 text-sm text-gray-500 mb-2">
        <Building2 className="w-4 h-4" />
        <span className="truncate">{store.brand}</span>
      </div>
      {store.address && (
        <div className="flex items-start gap-1.5 text-sm text-gray-500 mb-3">
          <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span className="truncate">
            {store.address.city}, {store.country}
          </span>
        </div>
      )}

      {/* Expandable details */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-1 text-sm text-gray-700 hover:text-gray-900 font-medium"
      >
        {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        {isExpanded ? 'Less details' : 'More details'}
      </button>

      {isExpanded && (
        <div className="mt-3 pt-3 border-t border-gray-100 space-y-2 text-sm">
          {store.champsNumber && (
            <div className="flex justify-between">
              <span className="text-gray-500">Champs #</span>
              <span className="text-gray-900">{store.champsNumber}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-gray-500">AIS Internal #</span>
            <span className="text-gray-900">{store.internalStoreNumber}</span>
          </div>
          {store.regionName && (
            <div className="flex justify-between">
              <span className="text-gray-500">Region</span>
              <span className="text-gray-900">{store.regionName}</span>
            </div>
          )}
          {store.timezone && (
            <div className="flex justify-between">
              <span className="text-gray-500">Timezone</span>
              <span className="text-gray-900">{store.timezone}</span>
            </div>
          )}
          {store.isTestStore && (
            <div className="mt-2">
              <span className="text-xs font-medium text-yellow-700 bg-yellow-100 px-2 py-1 rounded">
                Test Restaurant
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function StoresPage() {
  const { user } = useUserContext();
  const { data: storesData, isLoading, error, refetch } = useStoresQuery(!!user);

  // Memoize stores to avoid unnecessary re-renders
  const stores = useMemo(() => storesData?.data || [], [storesData?.data]);

  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [brandFilter, setBrandFilter] = useState<string>('all');

  // Build tree for tree view
  const treeRoot = useMemo(
    () => buildStoreHierarchyTree(stores, createMappStoreHierarchyOptions()),
    [stores]
  );

  // Get unique brands for filter
  const brands = useMemo(() => {
    const uniqueBrands = [...new Set(stores.map((s) => s.brand).filter(Boolean))];
    return uniqueBrands.sort();
  }, [stores]);

  // Filter stores
  const filteredStores = useMemo(() => {
    return stores.filter((store) => {
      const matchesSearch =
        searchQuery === '' ||
        store.storeName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        store.internalStoreNumber.includes(searchQuery) ||
        store.champsNumber?.includes(searchQuery);
      const matchesBrand = brandFilter === 'all' || store.brand === brandFilter;
      return matchesSearch && matchesBrand;
    });
  }, [stores, searchQuery, brandFilter]);

  const viewModes: { key: ViewMode; label: string; icon: React.ReactNode }[] = [
    { key: 'grid', label: 'Grid', icon: <Grid3X3 className="w-4 h-4" /> },
    { key: 'list', label: 'List', icon: <List className="w-4 h-4" /> },
    { key: 'tree', label: 'Tree', icon: <TreeDeciduous className="w-4 h-4" /> },
    { key: 'json', label: 'JSON', icon: <Globe className="w-4 h-4" /> },
  ];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Restaurants</h1>
        <p className="mt-1 text-sm text-gray-500">
          View and browse restaurants you have access to ({stores.length} total)
        </p>
      </div>

      {/* Search and filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or store number..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent"
          />
        </div>

        {/* Brand filter */}
        {brands.length > 1 && (
          <Autocomplete
            options={[
              { value: 'all', label: 'All Brands' },
              ...brands.map((b) => ({ value: b, label: b })),
            ]}
            value={brandFilter}
            onChange={(v) => setBrandFilter(v)}
            placeholder="Brand"
            clearable={false}
            className="min-w-[140px]"
          />
        )}

        {/* View mode toggle */}
        <div className="flex items-center bg-white border border-gray-200 rounded-lg p-1">
          {viewModes.map((mode) => (
            <button
              key={mode.key}
              onClick={() => setViewMode(mode.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                viewMode === mode.key
                  ? 'bg-gray-200 text-gray-900'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              title={mode.label}
            >
              {mode.icon}
              <span className="hidden sm:inline">{mode.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        // Skeleton loading state
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-start justify-between mb-3">
                <Skeleton className="w-10 h-10 rounded-lg" />
                <Skeleton className="w-16 h-6 rounded" />
              </div>
              <Skeleton className="h-5 w-3/4 mb-2" />
              <Skeleton className="h-4 w-1/2 mb-2" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          ))}
        </div>
      ) : error ? (
        <ErrorMessage
          message={(error as Error).message}
          title="Failed to load stores"
          onRetry={() => refetch()}
        />
      ) : filteredStores.length === 0 ? (
        // Empty state - different message based on whether filters are active
        searchQuery || brandFilter !== 'all' ? (
          <div className="bg-white rounded-xl border border-gray-200">
            <EmptySearchState
              query={searchQuery}
              entityName="stores"
              onClearFilters={() => {
                setSearchQuery('');
                setBrandFilter('all');
              }}
            />
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200">
            <EmptyState
              icon={StoreIcon}
              title="No stores available"
              description="You don't have access to any stores yet. Contact your administrator to request access."
            />
          </div>
        )
      ) : (
        <>
          {viewMode === 'grid' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredStores.map((store) => (
                <StoreCard key={store.internalStoreNumber} store={store} />
              ))}
            </div>
          )}

          {viewMode === 'list' && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="hidden sm:grid sm:grid-cols-[1fr_120px_150px_100px] gap-4 px-4 py-3 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500 uppercase tracking-wider">
                <div>Restaurant</div>
                <div>Restaurant #</div>
                <div>Brand</div>
                <div>Country</div>
              </div>
              <div className="divide-y divide-gray-200">
                {filteredStores.map((store) => (
                  <div
                    key={store.internalStoreNumber}
                    className="px-4 py-3 hover:bg-gray-50 transition-colors"
                  >
                    {/* Desktop row */}
                    <div className="hidden sm:grid sm:grid-cols-[1fr_120px_150px_100px] gap-4 items-center">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
                          <StoreIcon className="w-4 h-4 text-gray-600" />
                        </div>
                        <span className="font-medium text-gray-900 truncate">{store.storeName}</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-700">
                          {store.champsNumber || store.internalStoreNumber}
                        </span>
                        {store.champsNumber && (
                          <span className="text-[10px] text-gray-400">
                            AIS #{store.internalStoreNumber}
                          </span>
                        )}
                      </div>
                      <span className="text-sm text-gray-600">{store.brand}</span>
                      <span className="text-sm text-gray-600">{store.country}</span>
                    </div>
                    {/* Mobile row */}
                    <div className="sm:hidden">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
                          <StoreIcon className="w-4 h-4 text-gray-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{store.storeName}</p>
                          <p className="text-xs text-gray-500">
                            #{store.champsNumber || store.internalStoreNumber}
                            {store.champsNumber && (
                              <span className="text-gray-400">
                                {' '}· AIS #{store.internalStoreNumber}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-500 ml-11">
                        <span>{store.brand}</span>
                        <span>|</span>
                        <span>{store.country}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {viewMode === 'tree' && (
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <TreeView root={treeRoot} defaultExpandedDepth={3} />
            </div>
          )}

          {viewMode === 'json' && (
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <JsonViewer value={filteredStores} />
            </div>
          )}
        </>
      )}

      {/* Results summary */}
      {!isLoading && !error && filteredStores.length > 0 && (
        <div className="text-sm text-gray-500 text-center">
          Showing {filteredStores.length} of {stores.length} restaurants
        </div>
      )}
    </div>
  );
}
